import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Bot, Cpu, Wrench, GraduationCap, ListTodo, Activity as ActIcon, Link2, AlertTriangle, Loader2 } from "lucide-react";
import { WorkerControls } from "@/components/agents/WorkerControls";
import { apiGet } from "@/lib/api";
import { cn } from "@/lib/utils";

const STATUS_COLOR = { online: "#18c9ff", strained: "#ffd34e", offline: "#55778a", error: "#ff3f58" };

function Field({ icon: Icon, label, children, testId }) {
  return (
    <div data-testid={testId}>
      <div className="flex items-center gap-1.5 font-ui text-[11px] font-semibold uppercase tracking-wider text-jarvis-cyan/60 mb-1">
        {Icon && <Icon className="w-3.5 h-3.5" />} {label}
      </div>
      <div className="font-ui text-[14px] text-jarvis-cyanBright/85 pl-5">{children}</div>
    </div>
  );
}

const Unavailable = () => <span className="text-jarvis-cyanBright/35 italic">Unavailable</span>;

export function AgentDetailDrawer({ agent, onClose }) {
  const [state, setState] = useState("loading"); // loading | ready | error
  const [task, setTask] = useState(null);
  const [activity, setActivity] = useState([]);
  const [connections, setConnections] = useState([]);
  const [runtime, setRuntime] = useState(null);
  useEffect(() => { setRuntime(agent?.runtime || null); }, [agent]);

  useEffect(() => {
    if (!agent) return;
    let alive = true;
    setState("loading");
    Promise.all([
      apiGet("/tasks").catch(() => []),
      apiGet("/activity?limit=30").catch(() => []),
      apiGet(agent.workspace && agent.workspace !== "global" ? `/agents?workspace=${agent.workspace}` : "/agents").catch(() => []),
    ]).then(([tasks, acts, agents]) => {
      if (!alive) return;
      setTask((tasks || []).find((t) => t.agent_id === agent.id && t.status === "running") || null);
      setActivity((acts || []).filter((a) => (a.text || "").includes(agent.name)).slice(0, 5));
      const children = (agents || []).filter((a) => a.parent === agent.name).map((a) => a.name);
      const conns = [];
      if (agent.parent) conns.push({ dir: "reports to", name: agent.parent });
      children.forEach((c) => conns.push({ dir: "delegates to", name: c }));
      setConnections(conns);
      setState("ready");
    }).catch(() => alive && setState("error"));
    return () => { alive = false; };
  }, [agent]);

  const status = runtime ? runtime.alive ? "online" : runtime.state === "error" ? "error" : "offline" : agent?.status;
  const color = STATUS_COLOR[status] || "#55778a";
  const offline = status === "offline";

  return (
    <AnimatePresence>
      {agent && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[90] bg-black/60 backdrop-blur-sm" onClick={onClose} data-testid="agent-drawer-backdrop" />
          <motion.aside
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ type: "spring", stiffness: 260, damping: 30 }}
            className="fixed right-0 top-0 bottom-0 z-[91] w-full max-w-[400px] jv-panel hud-bracket border-l border-jarvis-cyan/30 overflow-y-auto"
            style={{ borderRadius: 0 }} role="dialog" aria-modal="true" aria-label={`Agent ${agent.name}`} data-testid="agent-drawer">
            <div className="flex items-center justify-between px-4 py-3 border-b border-jarvis-cyan/20 bg-jarvis-cyan/[0.04] sticky top-0 backdrop-blur">
              <div className="flex items-center gap-2 min-w-0">
                <Bot className="w-5 h-5 text-jarvis-cyan shrink-0" />
                <div className="min-w-0">
                  <div className="font-brand text-sm font-bold uppercase tracking-wider text-jarvis-cyanBright truncate">{agent.name}</div>
                  <div className="font-ui text-[12px] text-jarvis-cyanBright/50 truncate">{agent.role || "Agent"}</div>
                </div>
              </div>
              <button onClick={onClose} data-testid="agent-drawer-close" aria-label="Close"
                className="shrink-0 w-8 h-8 flex items-center justify-center border border-jarvis-cyan/25 text-jarvis-cyanBright/70 hover:bg-jarvis-cyan/10 hud-chamfer">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              <Field icon={Bot} label="Identity & State" testId="agent-field-state">
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: color, boxShadow: `0 0 8px ${color}` }} />
                  <span className="uppercase tracking-wide" style={{ color }}>{status}</span>
                  <span className="text-jarvis-cyanBright/50" data-testid="agent-load-not-connected">· load telemetry not connected</span>
                  <span className="text-jarvis-cyanBright/40 text-[12px]">· {agent.kind}</span>
                </div>
              </Field>

              <Field icon={ActIcon} label="Last Worker Heartbeat" testId="agent-field-heartbeat">{runtime?.heartbeat_at ? new Date(runtime.heartbeat_at).toLocaleTimeString() : <Unavailable />}</Field>

              <Field icon={Cpu} label="Model / Provider" testId="agent-field-model"><Unavailable /></Field>

              <Field icon={Wrench} label="Assigned Tools" testId="agent-field-tools">
                {agent.commands?.length ? (
                  <div className="flex flex-wrap gap-1.5">
                    {agent.commands.map((c) => (
                      <span key={c} className="font-num text-[11px] px-2 py-0.5 border border-jarvis-cyan/25 text-jarvis-cyanBright/75">{c}</span>
                    ))}
                  </div>
                ) : <span className="text-jarvis-cyanBright/40">No tools</span>}
              </Field>

              <Field icon={GraduationCap} label="Skills" testId="agent-field-skills"><Unavailable /></Field>

              <Field icon={ListTodo} label="Current Task" testId="agent-field-current-task">
                {state === "loading" ? <Skeleton /> : task ? (
                  <span>{task.title} <span className="text-jarvis-green text-[12px]">· {task.status}</span></span>
                ) : <span className="text-jarvis-cyanBright/40">No active task</span>}
              </Field>

              <Field icon={Link2} label="Connection Status" testId="agent-field-connection">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full" style={{ background: offline ? "#ff3f58" : "#18c9ff" }} />
                  <span>{offline ? "Worker stopped" : "App worker connected"}</span>
                  {agent.server && <span className="text-jarvis-cyanBright/45 text-[12px]">· {agent.server} is a label, not a remote connection</span>}
                </div>
                {connections.length > 0 && (
                  <div className="mt-1.5 space-y-0.5">
                    {connections.map((c) => (
                      <div key={`${c.dir}:${c.name}`} className="text-[12px] text-jarvis-cyanBright/55">
                        <span className="text-jarvis-cyan/60 uppercase tracking-wide">{c.dir}</span> → {c.name}
                      </div>
                    ))}
                  </div>
                )}
              </Field>

              <Field icon={ActIcon} label="Recent Activity" testId="agent-field-activity">
                {state === "loading" ? <Skeleton /> : state === "error" ? (
                  <span className="text-jarvis-red/80">Failed to load activity</span>
                ) : activity.length ? (
                  <div className="space-y-1">
                    {activity.map((a) => (
                      <div key={`${a.ts}:${a.workspace}:${a.text}`} className="font-num text-[11px] text-jarvis-cyanBright/55 leading-snug">{a.text}</div>
                    ))}
                  </div>
                ) : <span className="text-jarvis-cyanBright/40">No recent activity</span>}
              </Field>

              <WorkerControls key={agent.id} agentId={agent.id} agentName={agent.name} prefix="drawer-worker" onRuntime={setRuntime} />
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}

function Skeleton() {
  return <span className="inline-block w-32 h-3 rounded bg-jarvis-cyan/10 animate-pulse align-middle" />;
}

const RISK_COLOR = { LOW: "#18c9ff", MEDIUM: "#ffd34e", HIGH: "#ff3f58" };

export function ApprovalConfirmDialog({ approval, decision, submitting, onCancel, onConfirm }) {
  if (!approval) return null;
  const isApprove = decision === "approve";
  const created = approval.created_at ? new Date(approval.created_at).toLocaleString() : "—";
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" role="dialog" aria-modal="true" data-testid="approval-dialog">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={submitting ? undefined : onCancel} data-testid="approval-dialog-backdrop" />
      <div className={cn("relative w-full max-w-md jv-panel hud-chamfer hud-bracket", isApprove ? "border-jarvis-green/40" : "border-jarvis-red/40")}>
        <div className={cn("flex items-center gap-2.5 px-4 py-3 border-b", isApprove ? "border-jarvis-green/30 bg-jarvis-green/10" : "border-jarvis-red/30 bg-jarvis-red/10")}>
          <AlertTriangle className={cn("w-5 h-5", isApprove ? "text-jarvis-green" : "text-jarvis-red")} />
          <h3 className={cn("font-brand text-sm font-bold uppercase tracking-[0.14em]", isApprove ? "text-jarvis-green" : "text-jarvis-red")}>
            {isApprove ? "Confirm Approval" : "Confirm Denial"}
          </h3>
        </div>
        <div className="p-4 space-y-2.5 font-ui text-[14px] text-jarvis-cyanBright/85">
          <Row label="Action">{approval.action}</Row>
          <Row label="Target">{approval.workspace || "—"}</Row>
          <Row label="Consequences">{approval.reason || "—"}</Row>
          <Row label="Requester">{approval.agent || "—"}</Row>
          <Row label="Created">{created}</Row>
          <Row label="Expires">{approval.expires_at ? new Date(approval.expires_at).toLocaleString() : "—"}</Row>
          <Row label="Risk"><span style={{ color: RISK_COLOR[approval.risk] || "#8deaff" }}>{approval.risk || "—"}</span></Row>
          <div className="flex items-center justify-end gap-2.5 pt-2">
            <button onClick={onCancel} disabled={submitting} data-testid="approval-cancel-btn"
              className="font-brand text-[11px] font-semibold uppercase tracking-wider px-4 py-2 border border-jarvis-cyan/30 text-jarvis-cyanBright/80 hover:bg-jarvis-cyan/10 hud-chamfer disabled:opacity-40">
              Cancel
            </button>
            <button onClick={onConfirm} disabled={submitting} data-testid="approval-confirm-btn" autoFocus
              className={cn("flex items-center gap-1.5 font-brand text-[11px] font-bold uppercase tracking-wider px-4 py-2 border-2 hud-chamfer text-white disabled:opacity-60",
                isApprove ? "border-jarvis-green bg-jarvis-green/80 hover:bg-jarvis-green" : "border-jarvis-red bg-jarvis-red/85 hover:bg-jarvis-red")}>
              {submitting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              {submitting ? "Submitting…" : isApprove ? "Approve" : "Deny"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex gap-3">
      <span className="w-24 shrink-0 font-ui text-[11px] uppercase tracking-wider text-jarvis-cyan/55 pt-0.5">{label}</span>
      <span className="min-w-0 flex-1">{children}</span>
    </div>
  );
}
